import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import { boardActionEvent } from './taActionBoardAnalytics';
// These live in their own modules (this file is at its line budget), re-exported so
// `import * as api` keeps reaching them.
export * from './taHoldsApi';
export * from './taSlaSnoozeApi';
export * from './taCandidateNotesApi';
export * from './inviteDraftApi';
import { normalizeBoardPayload } from '../utils/taBoardPayload';
export {
  caseMaterialAsAttachment,
  fetchSentEmails,
  saveCaseMaterial,
  sendCandidateEmail,
  type CaseMaterial,
  type SentEmail,
} from './taOutboundApi';
import type {
  ActionRequest,
  EmailDraft,
  BoardResponse,
  GradedSlot,
  PanelConfig,
  Recruiter,
  TrackingEntry,
  TrackingKind,
  TrackingMap,
} from '../types/taActionBoard';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/ta-action-board`;

/** Re-exported: callers and tests import the naming rule from the API module. */
export { boardActionEvent } from './taActionBoardAnalytics';
/**
 * Final rounds now live in their own module (this file was over the 500-line ceiling), but every
 * existing caller imports them from here — re-exported so the split is invisible to them.
 */
export {
  bookFinalRound,
  fetchFinalRoundChains,
  saveFinalRoundConfig,
  type FinalRoundConfigInput,
} from './taFinalRoundApi';
/** Same split, same reason — the Slack ask calls live in taAskApi.ts. */
export {
  askPanelists,
  fetchInviteResponses,
  fetchSlackChannels,
  previewAskPanelists,
  type AskPreview,
  type PanelistAsk,
  type InviteResponseRow,
  type SlackChannel,
  type SlackChannelList,
} from './taAskApi';

export async function fetchRecruiters(): Promise<Recruiter[]> {
  const res = await authenticatedFetch(`${BASE}/recruiters`);
  const data = (await res.json()) as { recruiters: Recruiter[] };
  return data.recruiters;
}

/**
 * The board for one recruiter. Always normalized: the proxy deploys separately
 * from this frontend (and local dev talks to the PROD proxy), so a payload
 * missing a newer section must degrade to an empty section rather than take the
 * whole board down. See boardPayload.ts.
 */
/**
 * How long to wait for a board before giving up.
 *
 * A board build is many Lever calls plus a calendar read per panel, and on a
 * cold Cloud Run instance the first one of the day is the slowest. Without a
 * ceiling a stalled request span forever — recruiters saw a spinner that never
 * resolved and learned to hard-refresh. 90s is comfortably above a healthy
 * cold build and well below the point where anyone is still waiting.
 */
export const BOARD_TIMEOUT_MS = 90_000;

/**
 * Why the board could not be built. Mirrors `reason` from `lib/boardError.js`
 * on the proxy — see the contract documented there before adding one.
 */
export type BoardErrorReason =
  | 'no_lever_user'
  | 'timeout'
  | 'lever_forbidden'
  | 'lever_rate_limited'
  | 'lever_unavailable'
  | 'unknown';

/**
 * Retrying cannot conjure a Lever account, and offering a Retry button that is
 * guaranteed to fail is what taught the team to sit on the key until someone
 * came to help. Everything else is worth another go.
 */
const UNRETRYABLE: ReadonlySet<BoardErrorReason> = new Set<BoardErrorReason>(['no_lever_user']);

/** The proxy statuses that carry a typed reason; anything else stays a plain error. */
const TYPED_BOARD_STATUSES = [404, 503, 504];

const KNOWN_REASONS: ReadonlySet<string> = new Set<BoardErrorReason>([
  'no_lever_user',
  'timeout',
  'lever_forbidden',
  'lever_rate_limited',
  'lever_unavailable',
  'unknown',
]);

export class BoardLoadError extends Error {
  reason: BoardErrorReason;
  /** false when Retry provably cannot help — the view hides the button. */
  retryable: boolean;

  constructor(message: string, reason: BoardErrorReason) {
    super(message);
    this.name = 'BoardLoadError';
    this.reason = reason;
    this.retryable = !UNRETRYABLE.has(reason);
  }
}

/** An unrecognised reason is treated as retryable-generic, never as a crash. */
const asReason = (value: unknown): BoardErrorReason =>
  typeof value === 'string' && KNOWN_REASONS.has(value) ? (value as BoardErrorReason) : 'unknown';

/**
 * @param fresh  skip the proxy's cached copy and rebuild — what the Refresh button asks for when
 *   a board is already on screen.
 *
 *   ⚠️ **A retry after a FAILURE must not pass it**, and that is not a detail. The browser gives
 *   up on a build at `BOARD_TIMEOUT_MS`, but the proxy does not stop — it finishes and caches the
 *   board. So the attempt that "failed" is usually what makes the next one instant, and a retry
 *   that insisted on a rebuild would throw that away and start the same long build again. That
 *   loop, against a server cache that used to expire in 60 seconds, is what turned a slow board
 *   into an unloadable one.
 */
export async function fetchBoard(email: string, { fresh = false } = {}): Promise<BoardResponse> {
  try {
    const url =
      `${BASE}/board?email=${encodeURIComponent(email)}` + (fresh ? '&fresh=1' : '');
    const res = await authenticatedFetch(url, {
      signal: AbortSignal.timeout(BOARD_TIMEOUT_MS),
      // Handled here rather than by the shared error path, which flattens the
      // body to a message and drops `reason`. A 500 is deliberately NOT in the
      // list: an unclassified failure is a real crash and must keep reaching
      // the global error reporter.
      suppressErrorStatuses: TYPED_BOARD_STATUSES,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; reason?: string };
      const reason = asReason(body.reason);
      // Which failure modes recruiters actually hit, so the next rollout does
      // not depend on someone screenshotting the error. Category only — no
      // email, no candidate data.
      void logAnalyticsEvent('ta_board_load_failed', { reason, status: res.status });
      throw new BoardLoadError(body.error || 'Could not build the board.', reason);
    }
    return normalizeBoardPayload((await res.json()) as BoardResponse);
  } catch (e) {
    // A timeout is a distinct, actionable state — say so instead of surfacing
    // "signal is aborted without reason", which reads as a crash.
    if (e instanceof DOMException && (e.name === 'TimeoutError' || e.name === 'AbortError')) {
      void logAnalyticsEvent('ta_board_load_failed', { reason: 'timeout', status: 0 });
      throw new BoardLoadError(
        `The board took longer than ${Math.round(BOARD_TIMEOUT_MS / 1000)}s to build, so the page ` +
          'stopped waiting — but the server did not, and it keeps what it finishes. Try again in ' +
          'a moment and it is usually already there.',
        'timeout'
      );
    }
    throw e;
  }
}

export interface ActionResponse {
  ok: boolean;
  movedTo?: string;

  /** archive only: the recruiter approved a rejection email in the modal */
  emailApproved?: boolean;
  /** archive only: the approved rejection email actually went out */
  emailSent?: boolean;
  /** archive only: who it went to (echoed back for the toast) */
  to?: string;
  /** archive only: the filename attached to the rejection email that went out, if any */
  attachmentName?: string | null;
  /** archive only: the approved email was QUEUED for later rather than sent now */
  scheduled?: boolean;
  /** archive only: ISO time the queued rejection will be sent */
  scheduledFor?: string;
  /** archive only: why nothing was sent, when an email was approved */
  reason?: 'gmail-not-connected' | 'no-candidate-email' | 'mailbox-mismatch' | 'gmail-error' | 'unresolved-booking-link' | string;
}

export async function postAction(
  action: ActionRequest,
  // The board's shared Gmail token: with it, an archive's approved rejection
  // email becomes a draft in the caller's own mailbox instead of going to Make.
  gmailToken: string | null = null
): Promise<ActionResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (gmailToken) headers['X-Gmail-Token'] = gmailToken;
  const event = boardActionEvent(action.type);
  try {
    const res = await authenticatedFetch(`${BASE}/action`, {
      method: 'POST',
      headers,
      body: JSON.stringify(action),
    });
    const data = (await res.json()) as ActionResponse;
    // ONLY booleans derived from the request. `action` also carries an opportunityId, the
    // candidate's address and the full rejection email — none of which may travel. Whether an
    // email rode along is the interesting part anyway: it separates "archived silently" from
    // "archived with a rejection sent", which are different acts.
    void logAnalyticsEvent(event, {
      with_email: Boolean(action.email),
      with_gmail: Boolean(gmailToken),
    });
    return data;
  } catch (error) {
    // Failures get their own name so a broken action is visible as itself. Without this a flow
    // that fails every time looks identical to one nobody uses — both are simply absent.
    void logAnalyticsEvent(`${event}_failed`, {
      kind: error instanceof Error ? error.name : typeof error,
    });
    throw error;
  }
}

/** Re-render the rejection email (Archive modal): language toggle, or template switcher when `template` is set. */
export async function previewRejectionEmail(
  opportunityId: string,
  language: 'de' | 'en',
  template?: { category: string; key: string }
): Promise<EmailDraft> {
  const body = template ? { opportunityId, language, templateCategory: template.category, templateKey: template.key } : { opportunityId, language };
  const res = await authenticatedFetch(`${BASE}/rejection-email/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as { draft: EmailDraft };
  return data.draft;
}

export async function fetchTracking(opportunityIds: string[]): Promise<TrackingMap> {
  if (opportunityIds.length === 0) return {};
  const res = await authenticatedFetch(
    `${BASE}/tracking?opportunityIds=${encodeURIComponent(opportunityIds.join(','))}`
  );
  const data = (await res.json()) as { tracking: TrackingMap };
  return data.tracking;
}

export async function postTracking(
  opportunityId: string,
  entry: {
    kind: TrackingKind;
    slot?: string | null;
    slots?: string[] | null;
    slotMeta?: { label: string; grade: string }[] | null;
    note?: string | null;
  }
): Promise<{ ok: boolean; entry: TrackingEntry; status: string }> {
  const res = await authenticatedFetch(`${BASE}/tracking`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ opportunityId, entry }),
  });
  return res.json() as Promise<{ ok: boolean; entry: TrackingEntry; status: string }>;
}

/** Who a message comes from: the recruiter's own Slack, or the TA bot. */
export type NudgeSender = 'me' | 'bot';

/**
 * What a completed send reports. `mixed` exists because one nudge can reach several
 * interviewers and the personal token is resolved per DM — so the identities really can differ
 * within one send, and claiming either one would be a claim about messages it did not cover.
 */
export type NudgeSentAs = NudgeSender | 'mixed';

/**
 * Who would receive a feedback nudge, WHAT would be sent, and from WHICH identity. Nothing is
 * sent. The draft comes from the server so an unedited send is byte-identical to what was shown.
 */
export async function fetchNudgeRecipients(
  opportunityId: string,
  roleTitle?: string,
  /**
   * Omitted until the recruiter picks one, so the proxy derives it from the
   * role's tags. Naming it here OVERRIDES that.
   */
  language?: 'de' | 'en'
): Promise<{
  candidateName: string | null;
  stageName: string | null;
  recipients: { email: string; name: string | null }[];
  text: string;
  /** What the draft above is actually written in — what the toggle shows. */
  language?: 'de' | 'en';
  sender: NudgeSender;
  /** Is the per-recruiter Slack flow configured on this proxy at all? */
  canConnect?: boolean;
}> {
  const role = roleTitle ? `&roleTitle=${encodeURIComponent(roleTitle)}` : '';
  const lang = language ? `&language=${language}` : '';
  const res = await authenticatedFetch(
    `${BASE}/nudge-feedback?opportunityId=${encodeURIComponent(opportunityId)}${role}${lang}`
  );
  return res.json();
}

/**
 * DM the interviewers who owe feedback. Recipients are still resolved server-side from Lever;
 * `text` is the recruiter's own edit, escaped and capped by the proxy.
 */
export async function sendFeedbackNudge(payload: {
  opportunityId: string;
  roleTitle: string;
  text?: string;
  /** Only used when `text` is empty — the proxy builds its fallback in it. */
  language?: 'de' | 'en';
}): Promise<{ ok: boolean; sent: string[]; failed: string[]; sentAs: NudgeSentAs }> {
  const res = await authenticatedFetch(`${BASE}/nudge-feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

/**
 * Create the Lever interview for a case that was booked but never reached Lever.
 *
 * `already: true` means it was there all along — the board's copy was stale, not the write.
 */
export async function retryCaseLever(input: {
  opportunityId: string;
  /** Scopes the lookup to THIS role's case booking — the candidate may have others. */
  postingId: string;
  /** Whose board is on screen, so the right cached payload is dropped. */
  boardEmail: string;
}): Promise<{ ok: boolean; already?: boolean; panelId?: string }> {
  const res = await authenticatedFetch(`${BASE}/case-booking/lever-retry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.json();
}

export async function savePanelConfig(
  postingId: string,
  panel: PanelConfig
): Promise<{ ok: boolean; panel: PanelConfig }> {
  const res = await authenticatedFetch(`${BASE}/panel-config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ postingId, ...panel }),
  });
  return res.json() as Promise<{ ok: boolean; panel: PanelConfig }>;
}

export async function fetchSlots(
  postingId: string,
  /** Candidates currently waiting for this role — drives the server's suggestion cap (candidates + 2). */
  candidateCount = 1
): Promise<{
  panel: PanelConfig | null;
  slots: GradedSlot[];
  error: string | null;
  /** Why an empty window is empty, when the server can name a reason */
  diagnosis?: string | null;
  lookaheadDays?: number;
}> {
  const res = await authenticatedFetch(
    `${BASE}/slots?postingId=${encodeURIComponent(postingId)}&candidates=${encodeURIComponent(candidateCount)}`,
    // "Re-check" means re-read the calendars NOW. Express sends an ETag and no
    // Cache-Control on this route, so without this the browser is free to
    // answer a repeat click from its own cache and the button looks dead.
    { cache: 'no-store' }
  );
  return res.json() as Promise<{ panel: PanelConfig | null; slots: GradedSlot[]; error: string | null }>;
}

/**
 * Case-panel suggestion parsed from the role's kick-off sheet. The document is
 * read AS THE CALLER (X-Gmail-Token — same per-user consent as the Requests
 * tab), so the proxy can only reach sheets this recruiter already sees.
 */
/** The case panel read off a kick-off sheet. */
export interface CasePanelSuggestion {
  interviewers: string[];
  minutes: number | null;
}

/** 🏁 The final round's two seats, read off the sheet's final-round section. */
export interface FinalRoundSuggestion {
  finalInterviewer: string;
  barRaiser: string;
  interviewMinutes: number | null;
  /** Extra people the section named — the sheet cannot say which seat they take. */
  alsoFound?: string[];
}

/**
 * Read a suggestion out of the role's kick-off sheet. `stage` selects the
 * section: the case panel (default) or the final round.
 */
export async function fetchPanelSuggestion(
  postingId: string,
  gmailToken: string | null,
  stage: 'case' | 'final' = 'case'
): Promise<{
  kickoffUrl: string | null;
  suggestion: CasePanelSuggestion | FinalRoundSuggestion | null;
  unresolved?: string[];
  needsConnect?: boolean;
  reason?: string;
  error?: string;
  expired?: boolean;
}> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (gmailToken) headers['X-Gmail-Token'] = gmailToken;
  const res = await authenticatedFetch(`${BASE}/panel-suggest`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ postingId, stage }),
  });
  return res.json();
}

// ───────────────────────────── 🏁 FINAL ROUNDS ─────────────────────────────

/**
 * Candidate Requests live in their own module (this file was over the 500-line ceiling), but every
 * caller imports them from here — re-exported so the split is invisible.
 */
export * from './taInboundApi';

/**
 * The ask-panelists message as it would be sent, for the recruiter to read and
 * edit. Built by the SAME server code that sends it, so the preview and the
 * message cannot drift apart. Sends nothing.
 */
/**
 * The proxy build + draft format currently in production.
 *
 * Cheapest call in this file — no Lever, no Firestore, no model — and the board asks it before
 * trusting its localStorage copy. Without it a payload whose shape or content changed server-side
 * kept being rendered until someone happened to press Refresh.
 */
export async function fetchPayloadVersion(): Promise<string | null> {
  try {
    const res = await authenticatedFetch(`${BASE}/payload-version`);
    if (!res.ok) {
      // Not silent: with no version to compare, the cached board renders UNVERIFIED, and a
      // persistently failing endpoint would otherwise be indistinguishable from a healthy one.
      console.warn(`[ta-board] payload-version check failed (${res.status}) — cached board used unverified`);
      return null;
    }
    const data = (await res.json()) as { version?: string };
    return data.version || null;
  } catch (err) {
    // Degrade to "use whatever is cached": a version check that cannot run must never
    // stop a recruiter from seeing their board.
    console.warn('[ta-board] payload-version check unreachable — cached board used unverified', err);
    return null;
  }
}
